import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FetchError } from '@amzn/handshake-painter-typescript-client/v1';
import { useTheme } from '@amzn/meridian/theme';
import { useBundle } from '@amzn/react-arb-tools';
import { rio, rioDark } from '@amzn/rio-design-tokens';
import { ThemeProvider } from '@emotion/react';
import { useEffect, useState } from 'react';
import { matchPath, Outlet, useLocation, useSearchParams } from 'react-router';
import Column from '@amzn/meridian/column';
import { AlertProvider } from './common/alert';
import { AppError } from './common/errors';
import { Footer } from './common/footer';
import { Header } from './common/header';
import { BouncingBox } from './common/loaders';
import { queryClient } from './api/queryClient';
import { useAppInit, useContent, useProductDetails, useStorefront } from './hooks';
import { useOnAppInitSuccess } from './hooks/useOnAppInitSuccess';
import { useBagStore } from './store/bagStore';
import { ParzivalGatewayAuthErrorType } from './utils/auth';
import { getExperimentArm } from './utils/experiments';
import { redirectToLegacyPainter } from './utils/redirectUtils';
import { getRouteData, isRouteSupported, routes } from './utils/routes';
// TODO: Remove after full migration
const shouldRedirect = (pathname) => {
    // For unsupported routes, we want to redirect without showing the Painter 2.0 bouncing box
    if (!isRouteSupported(pathname)) {
        return 'unsupported_route';
    }
    return undefined;
};
export const App = () => {
    var _a, _b, _c;
    /**
     * The ThemeProvider from @emotion/react allows us to pass in the rio tokens and then access
     * them in the @emotion/styled components. The "mode" from "useTheme" is set in index.tsx
     */
    const { mode } = useTheme();
    const theme = mode === 'light' ? rio : rioDark;
    const location = useLocation();
    const [searchParams] = useSearchParams();
    // TODO: Remove hasStayParam check after full migration
    // The hasStayParam acts as an override so that we can test the experience on SMAs without affecting prod traffic
    const hasStayParam = searchParams.has('STAY_IN_PAINTER_2_DOT_0');
    const redirectReason = shouldRedirect(location.pathname);
    const shouldRedirectBeforeInit = !!redirectReason && !hasStayParam;
    /**
     * DO NOT CALL useAppInit in any other file within this components. If useAppInit fails and is called
     * downstream from here, then it will try to fetch again, causing an infinite loop.
     */
    const appData = useAppInit({ enabled: !shouldRedirectBeforeInit });
    const { isError, isLoading, error, data, isSuccess } = appData;
    const onAppInitSuccess = useOnAppInitSuccess();
    const { listBag } = useBagStore();
    const bagContents = listBag();
    const painter2LaunchArm = getExperimentArm(data === null || data === void 0 ? void 0 : data.experiments, 'WEBLAB_PAINTER_2_0_LAUNCH');
    /**
     * 1. For EXPIRED_AUTH error on init, stay in 2.0
     * 2. On any other error (networking, MISSING_AUTH, etc), redirect to 1.0
     */
    const errorCases = [ParzivalGatewayAuthErrorType.EXPIRED_AUTH, ParzivalGatewayAuthErrorType.MISSING_AUTH];
    const redirectOnError = error instanceof FetchError ? !errorCases.includes(error.cause.errorType) : isError;
    const shouldAppInitRedirect = redirectOnError || (isSuccess && painter2LaunchArm === 'C' && !hasStayParam);
    /**
     * TODO:
     *   - Follow this pattern for all pages
     *   - Move locale loading to this level
     */
    // Preload the route data
    const { route, bundleId } = getRouteData(location.pathname);
    const pdpData = useProductDetails({ enabled: route.isPDP && isSuccess && !shouldAppInitRedirect });
    const creatorData = useContent({ enabled: route.isCSF && isSuccess && !shouldAppInitRedirect });
    const storefrontData = useStorefront({ enabled: route.isSFP && isSuccess && !shouldAppInitRedirect });
    /*
      When the route changes, bundleId updates immediately but useBundle still returns the previous bundle
      for one render cycle (until its internal effect re-fires). We track the previous bundleId so we can
      detect this mismatch and show the loading state instead of rendering with stale/wrong translations.
    */
    const [bundle, isBundleLoading] = useBundle(bundleId);
    const [commonBundle, isCommonBundleLoading] = useBundle('pages.app');
    const [prevBundleId, setPrevBundleId] = useState(bundleId);
    const isBundleTransitioning = prevBundleId !== bundleId;
    useEffect(() => {
        setPrevBundleId(bundleId);
    }, [bundleId]);
    const isPageLoading = pdpData.isLoading ||
        creatorData.isLoading ||
        storefrontData.isLoading ||
        isBundleLoading ||
        isCommonBundleLoading ||
        isBundleTransitioning;
    // Determine if the current product is Prime eligible based on the route
    const isPrimeFromPDP = route.isPDP && ((_b = (_a = pdpData === null || pdpData === void 0 ? void 0 : pdpData.data) === null || _a === void 0 ? void 0 : _a.prime) === null || _b === void 0 ? void 0 : _b.isPrime);
    const isPrimeFromBag = (route.isPOP || route.isTYP || route.isPSP) && ((_c = bagContents[0]) === null || _c === void 0 ? void 0 : _c.isPrime);
    const isPrime = isPrimeFromPDP || isPrimeFromBag || false;
    /**
     * Clear the product details queries on location change to ensure that we are fetching the latest data.
     * This is tied to selecting product variations and usePrefetchProductDetails
     */
    useEffect(() => {
        if (!matchPath(routes.productDetailPage, location.pathname)) {
            queryClient.removeQueries({ predicate: (query) => query.queryKey.includes('getProductDetails') });
        }
    }, [location.pathname]);
    // Reset scroll on page transitions
    useEffect(() => {
        var _a;
        if (!((_a = location.state) === null || _a === void 0 ? void 0 : _a.preventScrollReset)) {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [location.pathname, location.state]);
    // AppInit success case
    useEffect(() => {
        if (isSuccess) {
            onAppInitSuccess(data);
        }
        // We perform this in a useEffect so that the BoucingBox continues to render while this logic is executing
        if (shouldAppInitRedirect) {
            redirectToLegacyPainter(redirectOnError ? 'app_init_error' : 'experiment_control_group');
        }
    }, [redirectOnError, isSuccess, shouldAppInitRedirect]);
    if (shouldRedirectBeforeInit) {
        redirectToLegacyPainter(redirectReason);
        return null;
    }
    if (isLoading || isPageLoading || shouldAppInitRedirect) {
        return _jsx(BouncingBox, {});
    }
    return (_jsxs(ThemeProvider, { theme: { mode, rio: theme }, children: [isError && (_jsx(Column, { "data-testid": "app-error", minHeight: "100vh", spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: _jsx(AppError, { error: error }) })), !isError && (_jsxs(Column, { "data-testid": "app", minHeight: "100vh", heights: ['fit', 'fill', 'fit'], spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: [_jsx(Header, { isPrime: isPrime, logosLabels: {
                            amazonLogo: commonBundle.getMessage('amazonLogo'),
                            primeLogo: commonBundle.getMessage('amazonPrimeLogo'),
                        } }), _jsx(AlertProvider, { children: _jsx(Outlet, { context: { appData, pdpData, creatorData, storefrontData, bundle, commonBundle } }) }, location.pathname), _jsx(Footer, { experiments: data === null || data === void 0 ? void 0 : data.experiments })] }))] }));
};
